import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
	IQuantDashboardState,
	ILastSelectedEntry,
	ISection,
	IGlobalDimensionDropdown,
	IGlobalDimension,
	IDashboardFilterDropdown,
	IDashboardCacheData,
	IFilterSelectionRestriction,
	IChartStub,
	ISlotError,
	IScaffoldMeta,
	ChartSlotStatusType,
	IChartSlotState,
	ICurrentAnalysisResetStateType
} from "../models/interfaces";

/** Default scaffold sub-state — used at init and on reset */
const initialScaffoldState: IScaffoldMeta = {
	loading: false,
	error: null,
	slideName: null,
	viewLayout: null,
	viewChartCount: 0,
	slots: {},
};

const initialState: IQuantDashboardState = {
	sections: [],
	activeSectionKey: undefined,
	selectedAnalysisId: undefined,
	selectedSlideName: undefined,
	globalFilters: [],
	localFilters: [],
	isFocusMode: false,
	globalFilterDropdownCatogeries: [],
	globalFilterDimensions: [],
	defaultFilter: {},
	overriddenFilter: {},
	isDashboardFilterLoading: false,
	isDashboardFilterOpen: false,
	quantDashbaordActiveInsightsId: null,
	searchOpen: false,
	defaultGlobalFilters: [],
	filterSelectionRestriction: null,
	chartTitle: "",
	isMultiTileSlide: false,
	scaffold: initialScaffoldState,
	lastSelectedMap: {},
	currentAnalysisResetState: {
		analysisId: null,
		slideName: null,
		isResetEnabled: null,
		isResetDisabled: null,
	},

};

const quantDashboardSlice = createSlice({
	name: "quantDashboard",
	initialState,
	reducers: {
		setSections(state, action: PayloadAction<ISection[]>) {
			state.sections = action.payload;
		},

		setActiveSection(state, action: PayloadAction<string>) {
			state.activeSectionKey = action.payload;
		},

		setSelectedAnalysisId(state, action: PayloadAction<string | undefined>) {
			state.selectedAnalysisId = action.payload;
		},
		setSelectedSlideName(state, action: PayloadAction<string | undefined>) {
			state.selectedSlideName = action.payload;
		},
		setGlobalFilters(state, action: PayloadAction<IDashboardFilterDropdown[]>) {
			state.globalFilters = action.payload;
		},
		setLocalFilters(state, action: PayloadAction<IDashboardFilterDropdown[]>) {
			state.localFilters = action.payload;
		},
		setIsFocusMode(state, action: PayloadAction<boolean>) {
			state.isFocusMode = action.payload;
		},
		setGlobalFilterDropdownCatogeries(state, action: PayloadAction<IGlobalDimensionDropdown[]>) {
			state.globalFilterDropdownCatogeries = action.payload;
		},
		setGlobalFilterDimensions(state, action: PayloadAction<IGlobalDimension[]>) {
			state.globalFilterDimensions = action.payload;
		},
		setDefaultFilter(state, action: PayloadAction<IDashboardCacheData>) {
			state.defaultFilter = action.payload;
		},
		setOverridenFilter(state, action: PayloadAction<IDashboardCacheData>) {
			state.overriddenFilter = action.payload;
		},
		setDashboardFilterLoading(state, action: PayloadAction<boolean>) {
			state.isDashboardFilterLoading = action.payload;
		},
		setIsDashboardFilterOpen(state, action: PayloadAction<boolean>) {
			state.isDashboardFilterOpen = action.payload;
		},
		setQuantDashboradActiveInsightsId(state, action: PayloadAction<string>) {
			state.quantDashbaordActiveInsightsId = action.payload;
		},
		setSearchOpen(state, action: PayloadAction<boolean>) {
			state.searchOpen = action.payload;
		},
		setDefaultGlobalFilters(state, action: PayloadAction<IDashboardFilterDropdown[]>) {
			state.defaultGlobalFilters = action.payload;
		},
		setFilterSelectionRestriction(state, action: PayloadAction<IFilterSelectionRestriction | null>) {
			state.filterSelectionRestriction = action.payload;
		},
		setChartTitle(state, action: PayloadAction<string>) {
			state.chartTitle = action.payload;
		},
		setIsMultiTileSlide(state, action: PayloadAction<boolean>) {
			state.isMultiTileSlide = action.payload;
		},

		/* ── Scaffold reducers ─────────────────────────────────── */

		/** Called before the scaffold API fires — resets scaffold sub-state */
		scaffoldLoadStart(state) {
			state.scaffold = {
				...initialScaffoldState,
				loading: true,
			};
		},

		/** Called on scaffold API success — populates stubs into slots */
		scaffoldLoadSuccess(
			state,
			action: PayloadAction<{
				slideName: string;
				viewLayout: string;
				viewChartCount: number;
				charts: IChartStub[];
			}>,
		) {
			const { slideName, viewLayout, viewChartCount, charts } = action.payload;
			state.scaffold.loading = false;
			state.scaffold.error = null;
			state.scaffold.slideName = slideName;
			state.scaffold.viewLayout = viewLayout;
			state.scaffold.viewChartCount = viewChartCount;

			// Initialize per-slot state from stubs.
			const slots: Record<number, IChartSlotState> = {};
			charts.forEach((stub, index) => {
				const position = stub.view_position ?? index + 1;
				const enrichedStub: IChartStub = { ...stub, view_position: position };
				slots[position] = {
					stub: enrichedStub,
					status: ChartSlotStatusType.IDLE,
					error: null,
					chartData: null,
				};
			});
			state.scaffold.slots = slots;
		},

		/** Called on scaffold API failure — blocks entire multichart view */
		scaffoldLoadFailed(state, action: PayloadAction<string>) {
			state.scaffold.loading = false;
			state.scaffold.error = action.payload;
		},

		/** Mark a specific slot as loading before /chart1 fires */
		slotLoadStart(state, action: PayloadAction<number>) {
			const slot = state.scaffold.slots[action.payload];
			if (slot) {
				slot.status = ChartSlotStatusType.LOADING;
				slot.error = null;
				slot.chartData = null;
			}
		},

		/** Slot /chart1 succeeded — store chart data for this position */
		slotLoadSuccess(state, action: PayloadAction<{ position: number; chartData: any }>) {
			const slot = state.scaffold.slots[action.payload.position];
			if (slot) {
				slot.status = ChartSlotStatusType.SUCCEEDED;
				slot.chartData = action.payload.chartData;
				slot.error = null;
			}
		},

		/** Slot /chart1 failed — store error for this position */
		slotLoadFailed(state, action: PayloadAction<{ position: number; error: ISlotError }>) {
			const slot = state.scaffold.slots[action.payload.position];
			if (slot) {
				slot.status = ChartSlotStatusType.FAILED;
				slot.error = action.payload.error;
			}
		},

		/** Full reset of scaffold sub-state (on unmount or navigation) */
		resetScaffold(state) {
			state.scaffold = initialScaffoldState;
		},

		/** Persist the last selected section + analysis for a given study */
		setLastSelected(state, action: PayloadAction<{ studyId: string; entry: ILastSelectedEntry }>) {
			state.lastSelectedMap[action.payload.studyId] = action.payload.entry;
		},

		/** Updates the reset-enabled flags for the currently active analysis/slide.
 *  Called after each chart API response (single-chart mode) or each slot
 *  response (scaffold mode) to keep the Reset button in sync with the
 *  latest backend state without a sections re-fetch. */
		setCurrentAnalysisResetState(
			state,
			action: PayloadAction<{
				analysisId: string | number | null;
				slideName: string | null;
				isResetEnabled?: boolean;
				isResetDisabled?: boolean;
			}>,
		) {
			state.currentAnalysisResetState = {
				analysisId: action.payload.analysisId,
				slideName: action.payload.slideName,
				isResetEnabled: action.payload.isResetEnabled ?? false,
				isResetDisabled: action.payload.isResetDisabled ?? true,
			};
		},

		/** Clears reset-state — used on analysis switch, after a reset action,
 *  and on unmount cleanup so stale flags don't leak across selections.
 *  Sets flags to null (unknown) so the UI falls back to sections data
 *  or waits for the next chart API response. */
		resetCurrentAnalysisResetState(state) {
			state.currentAnalysisResetState = {
				analysisId: null,
				slideName: null,
				isResetEnabled: null,
				isResetDisabled: null,
			};
		},
	},
});

export const {
	setSections,
	setActiveSection,
	setSelectedAnalysisId,
	setSelectedSlideName,
	setGlobalFilters,
	setLocalFilters,
	setIsFocusMode,
	setGlobalFilterDropdownCatogeries,
	setGlobalFilterDimensions,
	setDefaultFilter,
	setOverridenFilter,
	setDashboardFilterLoading,
	setIsDashboardFilterOpen,
	setQuantDashboradActiveInsightsId,
	setSearchOpen,
	setDefaultGlobalFilters,
	setFilterSelectionRestriction,
	setChartTitle,
	setIsMultiTileSlide,
	// Scaffold actions
	scaffoldLoadStart,
	scaffoldLoadSuccess,
	scaffoldLoadFailed,
	slotLoadStart,
	slotLoadSuccess,
	slotLoadFailed,
	resetScaffold,
	setLastSelected,
	setCurrentAnalysisResetState,
	resetCurrentAnalysisResetState,
} = quantDashboardSlice.actions;

export default quantDashboardSlice.reducer;
